import { apiClient } from './../client';
import { API_ENDPOINTS } from './../constant/endpoints';
import type { IApiResponse, IPaginatedResponse } from './../types';
import type {
    IUser,
    IUpdateProfileRequest,
    IChangePasswordRequest,
} from '@/features/auth/interfaces';

export const getMe = () =>
    apiClient.get<IApiResponse<IUser>>(API_ENDPOINTS.USER.ME).then((r) => r.data.data);

export const updateProfile = (data: IUpdateProfileRequest) =>
    apiClient.patch<IApiResponse<IUser>>(API_ENDPOINTS.USER.ME, data).then((r) => r.data.data);

export const changePassword = (data: IChangePasswordRequest) =>
    apiClient.patch<IApiResponse<null>>(API_ENDPOINTS.USER.PASSWORD, data).then((r) => r.data.data);

export interface IListUsersParams {
    readonly page?: number;
    readonly limit?: number;
    readonly search?: string;
    /**
     * Include OWNER-role members, who are hidden from non-owners by default.
     * Set only for read-only pickers that must resolve a stored user id to a
     * name — without it an admin viewing an owner-assigned config sees a UUID.
     */
    readonly includeOwners?: boolean;
}

export interface IListUsersResult {
    readonly data: readonly IUser[];
    readonly page: number;
    readonly limit: number;
    readonly total: number;
    readonly totalPages: number;
}

/**
 * Org-scoped paginated list of users. Backend `ApiResponse.paginated(...)`
 * returns `{ success, data, meta, message }` — mirrors the `tagsApi.list`
 * unwrap pattern so call sites get a flat shape.
 *
 * Throws `403` if the caller lacks `user.read.all`; the inbox filter drawer
 * catches that on its TanStack `useQuery` and falls back to the static
 * `[Anyone, Me, Unassigned]` picker.
 */
export const getAllUsers = (params?: IListUsersParams): Promise<IListUsersResult> =>
    apiClient
        .get<
            IApiResponse<IUser[]> & { meta: IPaginatedResponse<IUser> }
        >(API_ENDPOINTS.USER.ALL, { params })
        .then((r) => ({
            data: r.data.data,
            page: r.data.meta.page,
            limit: r.data.meta.limit,
            total: r.data.meta.total,
            totalPages: r.data.meta.totalPages,
        }));
